/**
 * @fileoverview PopoverContent component with viewport-aware positioning
 * @module shared/components/ui/ViewportAwarePopoverContent
 */

import { useEffect, useLayoutEffect, useRef, useState, type ReactElement, type ReactNode } from 'react'
import { createPortal } from 'react-dom'
import { cn } from '@/shared/lib/utils'

/**
 * Props for ViewportAwarePopoverContent component
 */
interface ViewportAwarePopoverContentProps {
  /** Whether the popover is open */
  open: boolean
  /** Trigger button reference for positioning */
  triggerRef: React.RefObject<HTMLElement | null>
  /** Additional CSS classes */
  className?: string
  /** Content to render inside the popover */
  children: ReactNode
  /** Estimated width of the popover */
  width?: number
  /** Estimated height of the popover */
  estimatedHeight?: number
  /** Offset from the trigger */
  offsetX?: number
  /** Offset from the trigger */
  offsetY?: number
  /** Callback when popover should close */
  onClose?: () => void
}

/**
 * PopoverContent component that ensures content stays within viewport boundaries.
 * Uses intelligent positioning and flipping to prevent cutoff at screen edges.
 *
 * @component
 * @example
 * ```tsx
 * <ViewportAwarePopoverContent
 *   open={isOpen}
 *   triggerRef={triggerRef}
 *   width={520}
 *   estimatedHeight={400}
 *   onClose={() => setOpen(false)}
 * >
 *   <div>Popover content</div>
 * </ViewportAwarePopoverContent>
 * ```
 */
export function ViewportAwarePopoverContent({
  open,
  triggerRef,
  className,
  children,
  width = 400,
  estimatedHeight = 300,
  offsetX = 16,
  offsetY = -16,
  onClose,
}: ViewportAwarePopoverContentProps): ReactElement | null {
  const [coords, setCoords] = useState({ left: 0, top: 0 })
  const [maxHeight, setMaxHeight] = useState<number>(estimatedHeight)
  const elementRef = useRef<HTMLDivElement | null>(null)

  // Calculate trigger button position and popover placement
  useLayoutEffect(() => {
    if (!open) return

    const updatePosition = (): void => {
      if (!triggerRef.current) return

      const triggerRect = triggerRef.current.getBoundingClientRect()

      // Prefer actual rendered height if available, fall back to estimate
      const contentHeight =
        elementRef.current?.getBoundingClientRect().height ?? estimatedHeight

      const viewportPadding = 8
      const maxLeft = window.innerWidth - width - viewportPadding
      const popoverMaxHeight = window.innerHeight - 2 * viewportPadding

      // Base vertical position aligned with trigger row
      let top = triggerRect.top + offsetY

      // Decide horizontal side: try right of trigger, otherwise flip to left
      const spaceRight = window.innerWidth - viewportPadding - triggerRect.right
      const spaceLeft = triggerRect.left - viewportPadding

      let left: number
      if (spaceRight >= width + offsetX) {
        // Enough room on the right – place popover to the right of the trigger
        left = triggerRect.right + offsetX
      } else if (spaceLeft >= width + offsetX) {
        // Not enough room on the right but enough on the left – place popover to the left
        left = triggerRect.left - width - offsetX
      } else {
        // Not enough space fully on either side – clamp within viewport while keeping near the trigger
        left = Math.min(
          Math.max(viewportPadding, triggerRect.right - width / 2),
          maxLeft
        )
      }

      // Effective height to use for vertical positioning
      const popoverHeightForPosition = Math.min(contentHeight, popoverMaxHeight)

      // Clamp vertically so popover always stays fully within viewport
      const maxTop = window.innerHeight - popoverHeightForPosition - viewportPadding
      top = Math.min(top, maxTop)
      top = Math.max(viewportPadding, top)

      // Ensure it doesn't go off left edge
      left = Math.max(viewportPadding, Math.min(left, maxLeft))

      setCoords({ left: Math.round(left), top: Math.round(top) })
      setMaxHeight(popoverMaxHeight)
    }

    updatePosition()

    // Recompute when the user scrolls or resizes so the popover stays anchored to the trigger
    window.addEventListener('scroll', updatePosition, true)
    window.addEventListener('resize', updatePosition)

    // Also observe content size changes (e.g., collapsing/expanding sections)
    let resizeObserver: ResizeObserver | undefined
    if (typeof ResizeObserver !== 'undefined') {
      resizeObserver = new ResizeObserver(() => {
        updatePosition()
      })
      if (elementRef.current) {
        resizeObserver.observe(elementRef.current)
      }
    }

    return () => {
      window.removeEventListener('scroll', updatePosition, true)
      window.removeEventListener('resize', updatePosition)
      if (resizeObserver) {
        resizeObserver.disconnect()
      }
    }
  }, [open, triggerRef, width, estimatedHeight, offsetX, offsetY])

  // Handle escape key to close
  useEffect(() => {
    const handleEscape = (event: KeyboardEvent): void => {
      if (event.key === 'Escape' && onClose) {
        onClose()
      }
    }

    if (open) {
      document.addEventListener('keydown', handleEscape)
      return () => document.removeEventListener('keydown', handleEscape)
    }
  }, [open, onClose])

  // Handle click outside to close
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent): void => {
      const target = event.target as Node
      
      // Check if click is inside this popover
      if (elementRef.current?.contains(target)) return
      
      // Check if click is on the trigger
      if (triggerRef.current?.contains(target)) return
      
      // Check if click is inside a nested Radix popover (e.g., CurrencySelect)
      // Radix popovers use portals with data-radix-popper-content-wrapper attribute
      const radixPopover = (target as Element).closest?.('[data-radix-popper-content-wrapper]')
      if (radixPopover) return
      
      onClose?.()
    }

    if (open) {
      document.addEventListener('mousedown', handleClickOutside)
      return () => document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [open, onClose, elementRef, triggerRef])

  if (!open) {
    return null
  }

  return createPortal(
    <div
      ref={elementRef}
      className={cn('rounded-2xl border border-border/20 bg-card p-5 shadow-2xl', className)}
      style={{
        position: 'fixed',
        zIndex: 50,
        left: coords.left,
        top: coords.top,
        width: `${width}px`,
        maxHeight: `${maxHeight}px`,
        overflowY: 'auto',
      }}
    >
      {children}
    </div>,
    document.body
  )
}
